import { Dispatch, Ref, SetStateAction, useContext, useEffect, useState } from 'react'
import { Button, FormHelperText, Grid } from '@material-ui/core'
import { AddressDetailsReadonly } from '@/components/atoms/AddressDetailsReadonly'
import { AutoFillVerificationDialog } from '@/components/atoms/AutoFillVerificationDialog'
import { useAutofillAddress } from '@/hooks/components/address-validation/useAutoFillAddress'
import { AddressValues, PreviousAddressDetailsValues, VerifyFreeformAddressResponse } from '@/types/v2/address'
import { capitalize } from '@/utils/helpers/address'
import { TStoreState } from '@/types/store/state'
import { useSelector } from 'react-redux'
import { SetValueAction } from 'react-select'
import WorkorderContext from '@/context/workorderContext'
import styles from './styles.module.css'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'

type ComponentProps = {
  /**
   * Reference
   */
  innerRef?: Ref<any>
  /**
   * Unique id value
   */
  internalFormId: string
  /**
   * Optional key to hide button
   */
  hideAddOrEditButton?: boolean
  /**
   * Optional key to custom initial button text
   */
  initialButtonText?: string
  /*
   * Optional key to disabled component
   */
  disabled?: boolean
  /**
   * Optional dialog title
   */
  dialogTitle?: string
  /**
   * Optional previous values
   */
  previousValues?: PreviousAddressDetailsValues
  /**
   * Error message
   */
  errorMessage?: string
  /**
   * Flag that indicates if form must be resetted
   */
  mustReset?: boolean
  /**
   * Callback to set values in Hook form
   */
  setFormValues?: (params: AddressValues) => void
  /***
   *
   */
  watch?: any
  /***
   *
   */
  setValue?: any
  /***
   *
   */
  getValues?: any
  /***
   *
   */
  showWarning?: boolean
}

export const AutoValidateAddressFields = ({
  internalFormId,
  innerRef,
  previousValues,
  dialogTitle,
  errorMessage,
  disabled,
  mustReset,
  hideAddOrEditButton,
  initialButtonText = 'Add New Address',
  setFormValues,
  watch,
  setValue,
  getValues,
  showWarning = true,
}: ComponentProps) => {
  const { states, isAdding, addressDetails, toggleIsAdding, setIsAdding } = useAutofillAddress({
    previousValues,
    mustReset,
  })

  const isDealerUpdatingAddr = useSelector(
    (state: TStoreState) => state.isUpdatingDealerAddress.isUpdatingDealerAddress
  )

  const buttonStyle = isDealerUpdatingAddr
    ? { backgroundColor: '#3f51b5', color: '#fff' }
    : { backgroundColor: '#fff', border: '1px solid #3f51b5', color: '#3f51b5' }

  const showReadonly = Boolean(addressDetails.streetAddress)

  const showFirstColumn = Boolean(errorMessage || showReadonly)
  const isVerified = watch('is_verified')
  const [isAddressVerified, setIsAddressVerified] = useState(isVerified || false)

  useEffect(() => {
    setIsAddressVerified(isVerified)
  }, [isVerified])

  return (
    <Grid container innerRef={innerRef ?? null}>
      {!isAddressVerified && showReadonly && showWarning && (
        <Grid
          item
          sm={12}
          className={styles.warningMsg}
          style={{ marginBottom: 20, textAlign: 'center', verticalAlign: 'middle' }}
        >
          <ErrorOutlineIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Address verification failed. Please update it to ensure accurate tax calculations.
        </Grid>
      )}
      {showFirstColumn && (
        <Grid item lg={8} xs={12} style={{ marginBottom: 20 }}>
          {errorMessage && (
            <FormHelperText error style={{ marginBottom: 20 }} className="input-helper-text">
              {errorMessage}
            </FormHelperText>
          )}

          {showReadonly && (
            <AddressDetailsReadonly
              disabled={disabled}
              streetAddress={addressDetails.streetAddress}
              extraInfo={addressDetails.extraInfo || '---'}
              city={addressDetails.city}
              county={addressDetails.county}
              state={addressDetails.state?.code || ''}
              zipCode={addressDetails.zipCode}
            />
          )}
        </Grid>
      )}
      {!hideAddOrEditButton && (
        <Grid
          container
          item
          lg={showFirstColumn ? 4 : undefined}
          xs={12}
          justifyContent={isDealerUpdatingAddr ? 'flex-start' : 'flex-end'}
        >
          <Grid
            item
            lg={showFirstColumn ? 12 : 4}
            md={showFirstColumn ? 12 : 7}
            sm={showFirstColumn ? 12 : 9}
            xs={12}
          >
            <Button
              fullWidth
              variant={!isDealerUpdatingAddr ? 'outlined' : 'contained'}
              color="primary"
              disabled={disabled}
              onClick={toggleIsAdding}
              style={buttonStyle}
              // style={!isDealerUpdatingAddr? backgroundColor: '#fff': ""}
            >
              {showReadonly ? 'Edit Address' : initialButtonText}
            </Button>
          </Grid>
        </Grid>
      )}
      {isAdding && (
        <AutoFillVerificationDialog
          id={internalFormId}
          dialogTitle={dialogTitle}
          previousValues={previousValues}
          states={states}
          onCancel={toggleIsAdding}
          setFormValues={setFormValues}
          setIsAdding={setIsAdding}
          watch={watch}
          getValues={getValues}
          setValue={setValue}
          // onConfirm={onConfirm}
        />
      )}
    </Grid>
  )
}
